import { useLazyQuery } from '@apollo/client'
import classnames from 'classnames'
import { NextPage } from 'next'
import {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react'
import { Subject } from 'rxjs'
import { debounceTime, distinctUntilChanged } from 'rxjs/operators'
import Layout from '../../components/Layout'
import {
  SONGS_LIST_QUERY,
  SONGS_SEARCH_PAGE_STATIC_DATA_QUERY
} from '../../gql/queries'
import { t } from '../../utils/index'
import { staticProps } from '../../utils/createApolloClient'
import {
  Keys,
  TimeSignature as TimeSignatureEnum
} from '../../utils/songs/enums'
import SongsList from './Components/SongsList'
import SongsListHeader from './Components/SongsListHeader'
import styles from './_songsPage.module.scss'
import { BlockScrollContext } from '../_app'
import { useRouter } from 'next/router'
import LoadingRing from '../../components/LoadingRing'
import useScrollLoading from '../../utils/hooks/useScrollLoading'
import dynamic from 'next/dynamic'
import isServer from '../../utils/isServer'
import {
  Genre,
  Difficulty,
  Voice,
  Vibe,
  Artist,
  Album,
  Theme,
  Song,
  SortSongsEnum,
  VibeEnum
} from '../../__generated__/graphql'
import type { ParsedUrlQuery } from 'querystring'

const AsideDynamic = dynamic(() => import('./Components/Aside'))

export const SearchContext = createContext<{
  filtersState: FiltersState
  setFiltersState: Dispatch<SetStateAction<FiltersState>>
  updateQueryParams: (filterState: FiltersState) => void
}>({ filtersState: null, setFiltersState: null, updateQueryParams: null })

export interface FiltersState {
  songKey?: string
  bpmRange?: number[]
  vibe: VibeEnum
  voice: number
  difficulties: string[]
  genres: number[]
  themes: number[]
  artists: number[]
  albums: number[]
  timeSignature?: string
  page: number
  perPage: number
  orderBy: SortSongsEnum
  favorite: boolean
  query: string
}

export const initalFiltersState: FiltersState = {
  favorite: false,
  genres: [],
  themes: [],
  artists: [],
  albums: [],
  voice: null,
  vibe: null,
  difficulties: [],
  bpmRange: null,
  page: 1,
  perPage: 20,
  orderBy: SortSongsEnum.CurrentlyTrending,
  query: '',
  songKey: null,
  timeSignature: null
}

interface Tag {
  id: number | string // difficulty can be string
  label: string
}

interface SearchInputTagProps {
  tag: string
  onTagRemove: any
}

const SearchInputTag: React.FC<SearchInputTagProps> = ({
  tag,
  onTagRemove
}) => {
  return (
    <div className={styles.searchInputTag}>
      <button className="button" onClick={onTagRemove}>
        <span>X</span>
        {tag}
      </button>
    </div>
  )
}

export interface SongPageStaticData {
  genres: Genre[]
  difficulties: Difficulty[]
  voices: Voice[]
  vibes: Vibe[]
  artists: Artist[]
  albums: Album[]
  themes: Theme[]
  timeSignatures: TimeSignatureEnum[]
}

export interface SongPageProps {
  staticData: SongPageStaticData
  songs: Song[]
}

export const filterStateToQueryVariables = (filterState: FiltersState) => {
  return {
    voiceId: filterState.voice,
    vibeId: filterState.vibe,
    songKey: Keys[filterState.songKey],
    genreIds: filterState.genres,
    themeIds: filterState.themes,
    artistIds: filterState.artists,
    albumIds: filterState.albums,
    difficultyIds: filterState.difficulties,
    bpmRange: filterState.bpmRange,
    timeSignature: filterState.timeSignature,
    page: 1,
    perPage: filterState.perPage * filterState.page,
    orderBy: filterState.orderBy,
    favorite: filterState.favorite,
    q: filterState.query
  }
}

const filterStateToRouterParams = (filterState: FiltersState) => {
  const params: any = {}
  if (filterState.albums?.length) params.albumIds = filterState.albums.join()
  if (filterState.artists?.length) params.artistIds = filterState.artists.join()
  if (filterState.bpmRange?.length)
    params.bpmRange = filterState.bpmRange.join()
  if (filterState.difficulties?.length)
    params.difficultyIds = filterState.difficulties.join()
  if (filterState.genres?.length) params.genreIds = filterState.genres.join()
  if (filterState.themes?.length) params.themeIds = filterState.themes.join()
  if (filterState.favorite) params.favorite = filterState.favorite
  if (filterState.orderBy) params.orderBy = filterState.orderBy
  if (filterState.query) params.q = filterState.query
  if (filterState.songKey) params.songKey = Keys[filterState.songKey]
  if (filterState.timeSignature)
    params.timeSignature = filterState.timeSignature
  if (filterState.vibe) params.vibeId = filterState.vibe
  if (filterState.voice) params.voiceId = filterState.voice

  return params
}

const queryParamsToFilterState = (
  queryParams: ParsedUrlQuery
): Partial<FiltersState> => {
  if (!queryParams) return initalFiltersState
  return {
    favorite: queryParams.favorite
      ? JSON.parse(queryParams.favorite as string)
      : initalFiltersState.favorite,
    genres:
      splitedArrayOfIds(queryParams.genreIds as string) ||
      initalFiltersState.genres,
    themes:
      splitedArrayOfIds(queryParams.themeIds as string) ||
      initalFiltersState.themes,
    artists:
      splitedArrayOfIds(queryParams.artistIds as string) ||
      initalFiltersState.artists,
    albums:
      splitedArrayOfIds(queryParams.albumIds as string) ||
      initalFiltersState.albums,
    voice: Number(queryParams.voiceId) || initalFiltersState.voice,
    vibe: (queryParams.vibeId as VibeEnum) || initalFiltersState.vibe,
    difficulties:
      splitedStringToArray(queryParams.difficultyIds as string) ||
      initalFiltersState.difficulties,
    bpmRange:
      splitedArrayOfIds(queryParams.bpmRange as string) ||
      initalFiltersState.bpmRange,
    orderBy:
      (queryParams.orderBy as SortSongsEnum) || initalFiltersState.orderBy,
    query: (queryParams.q as string) || initalFiltersState.query,
    songKey: Keys[queryParams.songKey as string] ?? initalFiltersState.songKey,
    timeSignature:
      (queryParams.timeSignature as string) || initalFiltersState.timeSignature
  }
}

const buildSearchInputTags = ({
  staticData,
  filtersState,
  onTagRemove
}: {
  staticData: SongPageStaticData
  filtersState: FiltersState
  onTagRemove: (key: string, id?: number | string) => void
}) => {
  const result = []

  const addSingleSearchInputTag = (key: string, val: any) => {
    result.push(
      <SearchInputTag
        key={`${key}-${val}`}
        tag={val}
        onTagRemove={() => {
          onTagRemove(key, val)
        }}
      />
    )
  }

  const addMultiSearchInputTag = (key: string, values: Tag[]) => {
    if (!values) {
      return
    }
    for (const tag of values) {
      result.push(
        <SearchInputTag
          key={`${key}-${tag.id}-${tag.label}`}
          tag={tag.label}
          onTagRemove={() => {
            onTagRemove(key, tag.id)
          }}
        />
      )
    }
  }

  const selectedIdsToTabs = ({ ids, collectionName }) => {
    if (!staticData) return []

    return staticData[collectionName]
      .filter((item) => ids.includes(item.id))
      .map((item) => ({ id: item.id, label: item.name.trim() }))
  }

  const selectedIdToTab = ({ id, collectionName }) => {
    if (!staticData) return []

    return staticData[collectionName].find((v) => v.id === id).name
  }

  for (const [key, value] of Object.entries(filtersState)) {
    if (!value && value !== 0) {
      continue
    }

    switch (key) {
      case 'songKey':
        addSingleSearchInputTag(key, Keys[value])
        break
      case 'bpmRange':
        if (value[0] !== 50 || value[1] !== 200) {
          addSingleSearchInputTag(key, `${value[0]}-${value[1]}bpm`)
        }
        break
      case 'difficulties':
      case 'artists':
      case 'albums':
      case 'themes':
      case 'genres':
        addMultiSearchInputTag(
          key,
          selectedIdsToTabs({ ids: value, collectionName: key })
        )
        break
      case 'voice':
      case 'vibe':
      case 'timeSignature':
        addSingleSearchInputTag(
          key,
          selectedIdToTab({ id: value, collectionName: `${key}s` })
        )
        break
      default:
        break
    }
  }
  return result
}

const SongsPage: NextPage<SongPageProps> = ({ staticData, songs }) => {
  const [filtersState, setFiltersState] = useState<FiltersState>({
    ...initalFiltersState
  })

  const [uploadSongPageAmount, setUploadSongPageAmount] = useState(1)
  const [view, setView] = useState('table')

  const [asideShown, setAsideShown] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [onSearch$] = useState(() => new Subject())
  const { setBlockScroll } = useContext(BlockScrollContext)
  const router = useRouter()

  const onQueryChange = ({ newQuery, oldQuery }) => {
    const oldLength = oldQuery.length
    setSearchInput(newQuery)

    const shouldSearch =
      (oldLength <= newQuery.length && newQuery.length > 2) ||
      oldLength > newQuery.length
    if (shouldSearch)
      updateQueryParams({ ...filtersState, query: newQuery, page: 1 })
  }

  const handleSearch = (e) => {
    onSearch$.next({ oldQuery: filtersState.query, newQuery: e.target.value })
  }

  const doSearch = (filterState) => {
    performSearch({ variables: filterStateToQueryVariables(filterState) })
  }

  const onTagRemove = (toRemove, id = null) => {
    switch (toRemove) {
      case 'artists':
      case 'albums':
      case 'genres':
      case 'difficulties':
      case 'themes':
        updateQueryParams({
          ...filtersState,
          page: 1,
          [toRemove]: filtersState[toRemove].filter((e) => e != id)
        })
        break
      default:
        updateQueryParams({
          ...filtersState,
          page: 1,
          [toRemove]: null
        })
    }
    doSearch(filtersState)
  }

  const toggleFilter = () => {
    setAsideShown(!asideShown)
  }
  const toggleView = (val) => setView(val)
  const searchInputTags = buildSearchInputTags({
    onTagRemove,
    staticData,
    filtersState
  })

  const [performSearch, { data: performSearchData, loading }] = useLazyQuery(
    SONGS_LIST_QUERY,
    {
      onCompleted: (data) => {
        if (!isServer()) {
          window.sessionStorage.setItem(
            'songsNumber',
            String(data.songs.length)
          )
          const scrollPosition = window.sessionStorage.getItem('scrollPosition')
          if (scrollPosition) {
            setTimeout(() => {
              window.scroll({
                top: +scrollPosition,
                behavior: 'smooth'
              })
              window.sessionStorage.removeItem('scrollPosition')
              window.sessionStorage.removeItem('songsNumber')
            }, 300)
          }
        }
      }
    }
  )

  const [searchResult, setSearchResult] = useState(songs || [])

  const songsListContainerRef = useRef(null)
  const doNewDataRequest = useRef(false)
  const initialLoad = useRef(true)

  const loadNewDataInBottomPosition = (songsLength) => {
    if (
      !doNewDataRequest.current &&
      songsLength > 0 &&
      songsLength % filtersState.perPage == 0
    ) {
      filtersState.page = uploadSongPageAmount + 1
      setUploadSongPageAmount((prev) => prev + 1)
      doSearch(filtersState)
    }
    doNewDataRequest.current = true
  }

  useEffect(() => {
    onSearch$
      .pipe(debounceTime(2), distinctUntilChanged())
      .subscribe(onQueryChange)

    const hasQueryParams = Object.keys(router.query).length
    if (!initialLoad.current || (initialLoad && hasQueryParams)) {
      doSearch({ ...filtersState, page: uploadSongPageAmount })
    } else initialLoad.current = false
  }, [filtersState])

  useScrollLoading(
    songsListContainerRef,
    () =>
      loadNewDataInBottomPosition(
        performSearchData?.songs.length || searchResult.length
      ),
    [performSearchData?.songs]
  )

  useEffect(() => {
    if (performSearchData) {
      setSearchResult(performSearchData.songs as Song[])
      doNewDataRequest.current = false
    }
  }, [performSearchData?.songs])

  useEffect(() => {
    if (router.query) {
      const paramsFromQuery = router.query || {}
      const queryParams = queryParamsToFilterState(paramsFromQuery)

      if (Object.keys(queryParams).length !== 0) {
        setFiltersState(() => ({
          ...initalFiltersState,
          ...queryParams
        }))
      }
      setSearchInput(queryParams.query)
    }
  }, [router.query])

  useEffect(() => {
    const songsNumber = window.sessionStorage.getItem('songsNumber')
    const scrollPosition = window.sessionStorage.getItem('scrollPosition')
    if (songsNumber && scrollPosition)
      doSearch({ ...filtersState, perPage: songsNumber }) //query previos number of pages if back from other route

    const onRouteChangeStart = () => {
      window.sessionStorage.setItem('scrollPosition', window.scrollY.toString())
    }

    router.events.on('routeChangeStart', onRouteChangeStart)

    return () => {
      router.events.off('routeChangeStart', onRouteChangeStart)
    }
  }, [])

  useEffect(() => {
    setBlockScroll(asideShown)
  }, [asideShown])

  const updateQueryParams = (newParams: FiltersState) => {
    router.push(
      {
        query: {
          ...filterStateToRouterParams(newParams)
        }
      },
      undefined,
      { shallow: true }
    )
  }

  return (
    <Layout
      pageTitle={t('WorshipOnline - Songs')}
      pageDescription={t(
        "Get instant access to video tutorials, lessons, chords and tabs (in any key) for hundreds of today's most popular worship songs."
      )}
    >
      <SearchContext.Provider
        value={{
          filtersState: filtersState,
          setFiltersState: setFiltersState,
          updateQueryParams: updateQueryParams
        }}
      >
        <div
          className={classnames(
            'columns is-gapless theme-color-wrapper',
            styles.songsContainer,
            {
              [styles.songsContainerWithoutAside]: !asideShown
            }
          )}
        >
          {asideShown && (
            <aside
              role="complementary"
              className={`is-3 is-4-tablet ${styles.aside}`}
            >
              <div className={styles.asideContainer}>
                <AsideDynamic
                  staticData={staticData}
                  someFilterSelected={!!searchInputTags.length}
                  setAsideShown={setAsideShown}
                />
              </div>
            </aside>
          )}

          <main role="search" className={`column ${styles.main}`}>
            <div className={styles.searchInputContainer}>
              <div className={styles.searchInputHolder}>
                <input
                  name="search"
                  className="input is-large'"
                  type="text"
                  value={searchInput}
                  onChange={handleSearch}
                  placeholder={t('Search for Songs, Artists, Albums')}
                />
              </div>
            </div>
            <div className={styles.searchInputTags}>{searchInputTags}</div>
            <div className={styles.songsList}>
              <SongsListHeader
                toggleAside={toggleFilter}
                asideShown={asideShown}
                toggleView={toggleView}
                view={view}
              />

              <div
                ref={songsListContainerRef}
                className={classnames(styles.songsListHolder, {
                  [styles.songsTableView]: view == 'table'
                })}
              >
                {searchResult.length ? (
                  <>
                    <SongsList
                      songs={searchResult}
                      isFlat={view == 'list'}
                      inList={view == 'list'}
                    />
                    {loading && <LoadingRing />}
                  </>
                ) : (
                  <div className={styles.placeholder}></div>
                )}
              </div>
            </div>
          </main>
        </div>
      </SearchContext.Provider>
    </Layout>
  )
}

const splitedArrayOfIds = (str: string) => {
  return str?.split(',').map((id) => Number(id))
}

const splitedStringToArray = (str: string) => {
  return str?.split(',')
}

export const getStaticProps = staticProps({
  additionalProps: async ({ client }) => {
    const {
      data: { staticData }
    } = await client.query({ query: SONGS_SEARCH_PAGE_STATIC_DATA_QUERY })
    const {
      data: { songs }
    } = await client.query({
      query: SONGS_LIST_QUERY,
      variables: filterStateToQueryVariables(initalFiltersState)
    })
    return {
      staticData,
      songs
    }
  }
})

export default SongsPage
